import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "@/redux/store";

// UI state interface
interface UIState {
  theme: "light" | "dark" | "auto";
}

// Initial state
const initialState: UIState = {
  theme: "auto", // Default theme, will be updated on client mount
};

// Create the UI slice
const uiSlice = createSlice({
  name: "ui",
  initialState,
  reducers: {
    setTheme: (state, action: PayloadAction<"light" | "dark" | "auto">) => {
      state.theme = action.payload;

      // Apply theme changes synchronously (fast DOM operation, won't block)
      if (typeof window !== "undefined") {
        // Persist theme to localStorage (fast operation, <1ms)
        try {
          localStorage.setItem("theme", action.payload);
        } catch (e) {
          // Silently fail if localStorage is unavailable
        }

        // Update data-theme attribute (fast DOM operation, <1ms)
        // This is synchronous but extremely fast and won't block rendering
        if (action.payload !== "auto") {
          document.documentElement.dataset.theme = action.payload;
        } else {
          // For auto mode, check system preference
          const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
          document.documentElement.dataset.theme = prefersDark ? "dark" : "light";
        }
      }
    },
  },
});

export const initializeTheme = () => {
  if (typeof window !== "undefined") {
    const savedTheme = localStorage.getItem("theme") || "auto";

    // Set up listener for system preference changes (only for "auto" mode)
    if (savedTheme === "auto") {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

      // Set initial theme based on system preference
      document.documentElement.dataset.theme = mediaQuery.matches ? "dark" : "light";

      // Listen for changes (only add listener once)
      const handleChange = (e: MediaQueryListEvent) => {
        document.documentElement.dataset.theme = e.matches ? "dark" : "light";
      };

      // Use addEventListener if available (modern browsers)
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener("change", handleChange);
      } else {
        // Fallback for older browsers
        mediaQuery.addListener(handleChange);
      }
    }
  }
};

// Export actions
export const { setTheme } = uiSlice.actions;

// Export selectors
export const selectTheme = (state: RootState) => state?.ui?.theme;

export default uiSlice.reducer;
